import { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import {
  Clock, Plus, Edit, Trash2, RefreshCw, Search, Calendar,
  User, MapPin, FileText, Filter, Download, Timer, Play, Square
} from 'lucide-react';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

export default function TimesheetsManagement() {
  const [timesheets, setTimesheets] = useState([]);
  const [handymen, setHandymen] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterHandyman, setFilterHandyman] = useState('all');
  const [filterJob, setFilterJob] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    handyman_id: '',
    job_id: '',
    punch_in_time: '',
    punch_out_time: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [timesheetsRes, handymenRes, jobsRes] = await Promise.all([
        axiosInstance.get('/timesheets/all'),
        axiosInstance.get('/handymen'),
        axiosInstance.get('/jobs')
      ]);
      setTimesheets(timesheetsRes.data);
      setHandymen(handymenRes.data);
      setJobs(jobsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterHandyman !== 'all') params.append('handyman_id', filterHandyman);
      if (filterJob !== 'all') params.append('job_id', filterJob);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const res = await axiosInstance.get(`/timesheets/all?${params}`);
      setTimesheets(res.data);
    } catch (error) {
      toast.error('Failed to filter data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTimesheet = async () => {
    if (!formData.handyman_id || !formData.job_id || !formData.punch_in_time || !formData.punch_out_time) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      await axiosInstance.post('/timesheets/manual', {
        handyman_id: formData.handyman_id,
        job_id: formData.job_id,
        punch_in_time: new Date(formData.punch_in_time).toISOString(),
        punch_out_time: new Date(formData.punch_out_time).toISOString(),
        notes: formData.notes,
        created_by: 'Admin'
      });
      
      toast.success('Timesheet created successfully');
      setShowAddDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create timesheet');
    }
  };

  const handleUpdateTimesheet = async () => {
    if (!selectedTimesheet) return;
    
    try {
      const updates = {};
      if (formData.punch_in_time) {
        updates.punch_in_time = new Date(formData.punch_in_time).toISOString();
      }
      if (formData.punch_out_time) {
        updates.punch_out_time = new Date(formData.punch_out_time).toISOString();
      }
      if (formData.notes !== undefined) {
        updates.notes = formData.notes;
      }
      
      await axiosInstance.patch(`/timesheets/${selectedTimesheet.id}`, updates);
      toast.success('Timesheet updated');
      setShowEditDialog(false);
      setSelectedTimesheet(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to update timesheet');
    }
  };

  const handleDeleteTimesheet = async (timesheetId) => {
    if (!confirm('Are you sure you want to delete this timesheet?')) return;
    
    try {
      await axiosInstance.delete(`/timesheets/${timesheetId}`);
      toast.success('Timesheet deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete timesheet');
    }
  };

  const resetForm = () => {
    setFormData({
      handyman_id: '',
      job_id: '',
      punch_in_time: '',
      punch_out_time: '',
      notes: ''
    });
  };

  const openEditDialog = (timesheet) => {
    setSelectedTimesheet(timesheet);
    setFormData({
      punch_in_time: timesheet.punch_in_time ? timesheet.punch_in_time.slice(0, 16) : '',
      punch_out_time: timesheet.punch_out_time ? timesheet.punch_out_time.slice(0, 16) : '',
      notes: timesheet.notes || ''
    });
    setShowEditDialog(true);
  };

  const filteredTimesheets = useMemo(() => {
    return timesheets.filter(ts => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (!ts.handyman_name?.toLowerCase().includes(term) &&
            !ts.job_name?.toLowerCase().includes(term) &&
            !ts.customer_name?.toLowerCase().includes(term)) {
          return false;
        }
      }
      return true;
    });
  }, [timesheets, searchTerm]);

  const stats = useMemo(() => {
    const completed = timesheets.filter(ts => ts.punch_out_time);
    const totalHours = completed.reduce((sum, ts) => sum + (ts.total_hours || 0), 0);
    const uniqueHandymen = new Set(timesheets.map(ts => ts.handyman_id)).size;
    
    return {
      total: timesheets.length,
      completed: completed.length,
      active: timesheets.filter(ts => !ts.punch_out_time).length,
      totalHours: totalHours.toFixed(2),
      avgHours: completed.length > 0 ? (totalHours / completed.length).toFixed(2) : 0,
      uniqueHandymen
    };
  }, [timesheets]);

  // Using imported formatDate, formatDateTime, formatTime from dateUtils

  const formatTimeLocal = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleTimeString('en-US', { timeZone: 'America/Phoenix', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div data-testid="timesheets-management">
      <div className="mb-8">
        <h2 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Timesheets Management
        </h2>
        <p className="text-gray-600">Track and manage handyman working hours</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="p-4">
            <div className="text-sm opacity-80">Total Entries</div>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
          <CardContent className="p-4">
            <div className="text-sm opacity-80">Completed</div>
            <div className="text-2xl font-bold">{stats.completed}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
          <CardContent className="p-4">
            <div className="text-sm opacity-80">Active Now</div>
            <div className="text-2xl font-bold">{stats.active}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="p-4">
            <div className="text-sm opacity-80">Total Hours</div>
            <div className="text-2xl font-bold">{stats.totalHours}h</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0">
          <CardContent className="p-4">
            <div className="text-sm opacity-80">Avg Hours</div>
            <div className="text-2xl font-bold">{stats.avgHours}h</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-0">
          <CardContent className="p-4">
            <div className="text-sm opacity-80">Handymen</div>
            <div className="text-2xl font-bold">{stats.uniqueHandymen}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
            <div className="space-y-2">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Handyman</Label>
              <Select value={filterHandyman} onValueChange={setFilterHandyman}>
                <SelectTrigger>
                  <SelectValue placeholder="All Handymen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Handymen</SelectItem>
                  {handymen.map(h => (
                    <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Job</Label>
              <Select value={filterJob} onValueChange={setFilterJob}>
                <SelectTrigger>
                  <SelectValue placeholder="All Jobs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Jobs</SelectItem>
                  {jobs.map(j => (
                    <SelectItem key={j.id} value={j.id}>{j.service_name} - {j.customer_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleFilter}>
                <Filter size={16} className="mr-2" />
                Filter
              </Button>
              <Button variant="outline" onClick={fetchData}>
                <RefreshCw size={16} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Button */}
      <div className="mb-4">
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus size={16} className="mr-2" />
              Add Manual Timesheet
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Manual Timesheet</DialogTitle>
              <DialogDescription>Create a new timesheet entry manually</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Handyman *</Label>
                <Select
                  value={formData.handyman_id}
                  onValueChange={(value) => setFormData({ ...formData, handyman_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select handyman" />
                  </SelectTrigger>
                  <SelectContent>
                    {handymen.map(h => (
                      <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Job *</Label>
                <Select
                  value={formData.job_id}
                  onValueChange={(value) => setFormData({ ...formData, job_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select job" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.map(j => (
                      <SelectItem key={j.id} value={j.id}>
                        {j.service_name} - {j.customer_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.punch_in_time}
                    onChange={(e) => setFormData({ ...formData, punch_in_time: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.punch_out_time}
                    onChange={(e) => setFormData({ ...formData, punch_out_time: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  placeholder="Additional notes..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
              <Button className="w-full" onClick={handleAddTimesheet}>
                Create Timesheet
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Timesheets Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#595959]"></div>
        </div>
      ) : (
        <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Handyman</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Start Time</TableHead>
                  <TableHead>End Time</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTimesheets.map((ts) => (
                  <TableRow key={ts.id}>
                    <TableCell>{formatDate(ts.punch_in_time)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-gray-400" />
                        <span className="font-medium">{ts.handyman_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{ts.job_name || 'General'}</p>
                        {ts.customer_name && (
                          <p className="text-xs text-gray-500">{ts.customer_name}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Play size={12} className="text-green-500" />
                        {formatTime(ts.punch_in_time)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {ts.punch_out_time ? (
                        <div className="flex items-center gap-1">
                          <Square size={12} className="text-red-500" />
                          {formatTime(ts.punch_out_time)}
                        </div>
                      ) : (
                        <span className="text-orange-500">In Progress</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {ts.total_hours ? `${ts.total_hours}h` : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {ts.punch_out_time ? (
                          <Badge className="bg-green-500 text-white">Completed</Badge>
                        ) : (
                          <Badge className="bg-orange-500 text-white">Active</Badge>
                        )}
                        {ts.is_manual && (
                          <Badge variant="outline">Manual</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(ts)}
                        >
                          <Edit size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500"
                          onClick={() => handleDeleteTimesheet(ts.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {filteredTimesheets.length === 0 && (
              <div className="text-center py-12">
                <Clock size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No timesheet entries found</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Timesheet</DialogTitle>
            <DialogDescription>
              {selectedTimesheet?.handyman_name} - {formatDate(selectedTimesheet?.punch_in_time)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input
                  type="datetime-local"
                  value={formData.punch_in_time}
                  onChange={(e) => setFormData({ ...formData, punch_in_time: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input
                  type="datetime-local"
                  value={formData.punch_out_time}
                  onChange={(e) => setFormData({ ...formData, punch_out_time: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Notes..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
            <Button className="w-full" onClick={handleUpdateTimesheet}>
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
